// Faixas da aba Final — renderChips do edvid, uma faixa por TIPO (legendas, texto,
// animações, atrás da pessoa, imagens/tela dividida, transições, trilha) com auto-lanes
// quando itens se sobrepõem. Editar aqui muda as props da composição e o Player mostra na hora.
import { useMemo, useRef, type ReactNode, type RefObject } from 'react';
import type { CaptionWord } from '../../../types/videoEdit';
import type { VisualProps } from './demoProps';
import { assignLanes, fmt, snapTo } from './edvidTime';
import { TimelineShell, TrackRow, useTimeline } from './TimelineShell';
import { NotesLane } from './notes';
import type { Playhead } from './playhead';
import type { EdvidNote } from '../../../types/videoEdit';
import { IconCaptions, IconFlag, IconFlash, IconImage, IconMusic, IconText } from './icons';
import { graphicSummary, type GraphicLike } from '../../../video-compositions/graphics/schema';

// ícones das faixas novas (mesmo desenho sólido 16×16 de icons.tsx)
const IconGraphic = () => (
  <svg viewBox="0 0 16 16" width={14} height={14} fill="currentColor" aria-hidden>
    <rect x="1.5" y="9" width="3" height="5.5" rx="1" /><rect x="6.5" y="5.5" width="3" height="9" rx="1" /><rect x="11.5" y="2" width="3" height="12.5" rx="1" />
  </svg>
);
const IconBehind = () => (
  <svg viewBox="0 0 16 16" width={14} height={14} fill="currentColor" aria-hidden>
    <rect x="1" y="2" width="14" height="9" rx="2" opacity=".45" />
    <circle cx="8" cy="7" r="2.6" /><path d="M3.4 15c.5-3 2.4-4.6 4.6-4.6s4.1 1.6 4.6 4.6z" />
  </svg>
);

export type FinalSel =
  | { kind: 'caption'; line: number }
  | { kind: 'hook' }
  | { kind: 'split'; index: number }
  | { kind: 'insert'; index: number }
  | { kind: 'graphic'; index: number }
  | { kind: 'behind'; index: number }
  | null;

export interface CaptionLine { text: string; start: number; end: number; words: number[] }

/** Linhas de ≤3 palavras que quebram na pontuação — o agrupamento do template (groupCaptions do edvid). */
export function groupCaptions(caps: CaptionWord[], maxWords = 3): CaptionLine[] {
  const lines: CaptionLine[] = [];
  let cur: number[] = [];
  const flush = () => {
    if (!cur.length) return;
    lines.push({
      text: cur.map(i => caps[i].text.replace(/[.,!?…]+$/, '')).join(' '),
      start: caps[cur[0]].startMs / 1000,
      end: caps[cur[cur.length - 1]].endMs / 1000,
      words: cur,
    });
    cur = [];
  };
  caps.forEach((w, i) => {
    cur.push(i);
    if (cur.length >= maxWords || /[.,!?…]$/.test(w.text)) flush();
  });
  flush();
  return lines;
}

type DragMode = 'move' | 'l' | 'r';

/** Chip arrastável/aparável. `snap` recebe o tempo candidato e devolve o tempo imantado. */
function Chip({ start, end, label, selected, tone, handles, onSelect, onBegin, onChange, onEnd, snap, min = 0.15, max, title, dirty }: {
  start: number; end: number; label: ReactNode; selected: boolean; tone: string;
  handles: DragMode[];
  onSelect: () => void;
  onBegin?: () => void;
  onChange?: (start: number, end: number) => void;
  onEnd?: () => void;
  snap?: (t: number) => number;
  min?: number; max: number; title?: string; dirty?: boolean;
}) {
  const { pps, showTip, hideTip } = useTimeline();
  const drag = useRef<{ mode: DragMode; x0: number; s: number; e: number } | null>(null);
  const down = (e: React.PointerEvent, mode: DragMode) => {
    e.stopPropagation();
    onSelect();
    if (!handles.includes(mode) || !onChange) return;
    e.preventDefault();
    drag.current = { mode, x0: e.clientX, s: start, e: end };
    onBegin?.();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* sintético */ }
  };
  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || !onChange) return;
    const dt = (e.clientX - d.x0) / pps;
    const sn = snap || ((t: number) => t);
    let s = d.s; let en = d.e;
    if (d.mode === 'move') {
      const dur = d.e - d.s;
      s = Math.max(0, Math.min(max - dur, d.s + dt));
      const snapped = sn(s);
      const snappedEnd = sn(s + dur);
      if (snapped !== s) s = snapped; else if (snappedEnd !== s + dur) s = snappedEnd - dur;
      s = Math.max(0, Math.min(max - dur, s));
      en = s + dur;
    } else if (d.mode === 'l') {
      s = Math.min(Math.max(0, sn(d.s + dt)), d.e - min);
    } else {
      en = Math.max(Math.min(max, sn(d.e + dt)), d.s + min);
    }
    onChange(s, en);
    showTip(e.clientX, e.clientY, <span>{fmt(s)} → {fmt(en)} <span className="text-slate-400">({(en - s).toFixed(2)}s)</span></span>);
  };
  const up = () => { if (drag.current) { drag.current = null; onEnd?.(); } hideTip(); };
  return (
    <div
      data-tl-item
      onPointerDown={e => down(e, 'move')}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      title={title}
      className={`group absolute bottom-[3px] top-[3px] flex items-center overflow-hidden rounded-md border px-1.5 text-[10px] font-bold ${tone} ${selected ? 'z-[5] ring-2 ring-indigo-400' : ''} ${dirty ? 'border-dashed' : ''} ${handles.includes('move') ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
      style={{ left: start * pps, width: Math.max((end - start) * pps, 8) }}
    >
      <span className="pointer-events-none truncate">{label}</span>
      {(['l', 'r'] as const).filter(m => handles.includes(m)).map(m => (
        <div
          key={m}
          data-tl-item
          onPointerDown={e => down(e, m)}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          className={`absolute bottom-0 top-0 w-2 cursor-ew-resize group-hover:bg-white/30 ${m === 'l' ? 'left-0' : 'right-0'}`}
        />
      ))}
    </div>
  );
}

export function FinalTimeline({ props, duration, sel, onSelect, panelRef, pps, playhead, onSeek, notes, pendingIn, onOpenNote, onBegin, onGesture, onEnd, savedProps }: {
  props: VisualProps;
  savedProps: VisualProps;
  duration: number;
  sel: FinalSel;
  onSelect: (s: FinalSel) => void;
  panelRef: RefObject<HTMLDivElement>;
  pps: number;
  playhead: Playhead;
  onSeek: (t: number) => void;
  notes: EdvidNote[];
  pendingIn: number | null;
  onOpenNote: (id: string) => void;
  onBegin: () => void;
  onGesture: (fn: (p: VisualProps) => VisualProps) => void;
  onEnd: () => void;
}) {
  const ed = props.editData;
  const lines = useMemo(() => groupCaptions(props.captions, ed.captions.maxWords || 3), [props.captions, ed.captions.maxWords]);
  const cuePts = useMemo(() => {
    const pts = [0, duration];
    for (const s of props.segments.segments) pts.push(s.start, s.start + s.dur);
    return pts;
  }, [props.segments, duration]);
  const frozen = useRef<Map<string, number> | null>(null);
  function lanesFor<T extends { start: number; end: number }>(items: T[], key: (it: T) => string) {
    const { laneOf } = assignLanes(items);
    const get = (it: T) => frozen.current?.get(key(it)) ?? laneOf.get(it) ?? 0;
    const lanes = Math.max(1, ...items.map(it => get(it) + 1));
    const snapshot = () => items.map(it => [key(it), get(it)] as [string, number]);
    return { lanes, get, snapshot };
  }
  const tol = 8 / pps;
  const snap = (t: number) => snapTo(t, cuePts, tol);
  // animações e "atrás da pessoa" imantam também nas bordas das palavras (a fala manda)
  const wordPts = useMemo(() => {
    const pts = [...cuePts];
    for (const w of props.captions) pts.push(w.startMs / 1000, w.endMs / 1000);
    return pts;
  }, [cuePts, props.captions]);
  const snapWord = (t: number) => snapTo(t, wordPts, tol);

  const graphics = (ed.graphics || []).map((g, index) => ({ g, index, start: +g.start, end: +g.end }));
  const gL = lanesFor(graphics, it => `g${it.index}`);
  const savedGraphics = savedProps.editData.graphics || [];
  const behind = (ed.behind || []).map((b, index) => ({ b, index, start: +b.start, end: +b.start + +b.dur }));
  const bL = lanesFor(behind, it => `b${it.index}`);
  const savedBehind = savedProps.editData.behind || [];
  const setGraphic = (index: number, start: number, end: number) => onGesture(p => {
    const arr = [...(p.editData.graphics || [])];
    arr[index] = { ...arr[index], start: +start.toFixed(3), end: +end.toFixed(3) };
    return { ...p, editData: { ...p.editData, graphics: arr } };
  });
  const setBehind = (index: number, start: number, end: number) => onGesture(p => {
    const arr = [...(p.editData.behind || [])];
    arr[index] = { ...arr[index], start: +start.toFixed(3), dur: +(end - start).toFixed(3) };
    return { ...p, editData: { ...p.editData, behind: arr } };
  });

  const images = [
    ...(ed.splitInserts || []).map((it, index) => ({ kind: 'split' as const, index, start: +it.start, end: +it.end, src: it.src, layout: it.layout })),
    ...(ed.inserts || []).map((it, index) => ({ kind: 'insert' as const, index, start: +it.start, end: +it.end, src: it.src, layout: undefined })),
  ];
  const iL = lanesFor(images, it => `${it.kind}${it.index}`);
  // durante um arrasto as lanes ficam CONGELADAS: trocar de lane desmonta o chip no meio do
  // gesto (perde o pointer capture, o pointerup nunca chega e o desfazer não empilha)
  const beginDrag = () => { frozen.current = new Map([...gL.snapshot(), ...bL.snapshot(), ...iL.snapshot()]); onBegin(); };
  const endDrag = () => { frozen.current = null; onEnd(); };
  const savedSplit = savedProps.editData.splitInserts || [];
  const savedIns = savedProps.editData.inserts || [];

  const setImage = (kind: 'split' | 'insert', index: number, start: number, end: number) => onGesture(p => {
    const e = p.editData;
    if (kind === 'split') {
      const arr = [...(e.splitInserts || [])];
      const group = arr[index]?.group;
      const before = arr[index];
      for (let i = 0; i < arr.length; i++) {
        if (i === index || (group && arr[i].group === group)) arr[i] = { ...arr[i], start: +start.toFixed(3), end: +end.toFixed(3) };
      }
      const windows = e.captions.windows?.map(w => w.start === before.start && w.end === before.end ? { ...w, start: +start.toFixed(3), end: +end.toFixed(3) } : w);
      return { ...p, editData: { ...e, splitInserts: arr, captions: { ...e.captions, ...(windows ? { windows } : {}) } } };
    }
    const arr = [...e.inserts];
    arr[index] = { ...arr[index], start: +start.toFixed(3), end: +end.toFixed(3) };
    return { ...p, editData: { ...e, inserts: arr } };
  });

  const isSel = (s: FinalSel) => JSON.stringify(s) === JSON.stringify(sel);
  const fname = (src: string) => decodeURIComponent((src || '').split('/').pop() || 'imagem');

  return (
    <TimelineShell duration={duration} pps={pps} panelRef={panelRef} playhead={playhead} onSeek={onSeek} className="shrink-0">
      <TrackRow icon={<IconFlag />} tone="rose" title="Marcações de ajuste" height={24}>
        <NotesLane notes={notes} pendingIn={pendingIn} onOpen={onOpenNote} />
      </TrackRow>
      <TrackRow icon={<IconCaptions />} tone="teal" title="Legendas" height={28}>
        {ed.captions.enabled && lines.map((l, i) => (
          <Chip
            key={i}
            start={l.start}
            end={l.end}
            max={duration}
            label={l.text}
            title={`${l.text}\n${fmt(l.start)} → ${fmt(l.end)}\nclique para corrigir as palavras`}
            selected={isSel({ kind: 'caption', line: i })}
            tone="border-teal-400/40 bg-teal-500/15 text-teal-100"
            handles={[]}
            onSelect={() => { onSelect({ kind: 'caption', line: i }); }}
          />
        ))}
        {!ed.captions.enabled && <span className="absolute left-2 top-1.5 text-[10px] text-slate-600">legenda desligada</span>}
      </TrackRow>
      <TrackRow icon={<IconText />} tone="violet" title="Texto (gancho)" height={28}>
        {ed.hook.enabled && (
          <Chip
            start={0}
            end={ed.hook.endSec}
            max={duration}
            min={0.5}
            label={`GANCHO — ${(ed.hook.lines?.length ? ed.hook.lines : [ed.hook.text || '']).join(' / ')}`}
            title="Arraste a borda direita para mudar quando o gancho sai"
            selected={isSel({ kind: 'hook' })}
            dirty={ed.hook.endSec !== savedProps.editData.hook.endSec}
            tone="border-violet-400/50 bg-violet-500/20 text-violet-100"
            handles={['r']}
            snap={snap}
            onSelect={() => onSelect({ kind: 'hook' })}
            onBegin={onBegin}
            onChange={(_s, e) => onGesture(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, endSec: +e.toFixed(3) } } }))}
            onEnd={onEnd}
          />
        )}
      </TrackRow>
      {Array.from({ length: graphics.length ? gL.lanes : 1 }).map((_, lane) => (
        <TrackRow key={`gfx${lane}`} icon={lane === 0 ? <IconGraphic /> : undefined} tone="indigo" title="Animações" height={28}>
          {graphics.filter(it => gL.get(it) === lane).map(({ g, index, start, end }) => {
            const saved = savedGraphics.find(x => x.id === g.id);
            const summary = graphicSummary(g as GraphicLike);
            return (
              <Chip
                key={`g${index}`}
                start={start}
                end={end}
                max={duration}
                min={0.5}
                label={summary}
                title={`${summary}
${fmt(start)} → ${fmt(end)}
arraste para mover · bordas aparam · ímã nas palavras e emendas`}
                selected={isSel({ kind: 'graphic', index })}
                dirty={!saved || saved.start !== g.start || saved.end !== g.end || saved.template !== g.template || JSON.stringify(saved.props) !== JSON.stringify(g.props) || saved.zone !== g.zone}
                tone="border-fuchsia-400/50 bg-fuchsia-500/15 text-fuchsia-100"
                handles={['move', 'l', 'r']}
                snap={snapWord}
                onSelect={() => onSelect({ kind: 'graphic', index })}
                onBegin={beginDrag}
                onChange={(s, e) => setGraphic(index, s, e)}
                onEnd={endDrag}
              />
            );
          })}
          {!graphics.length && <span className="absolute left-2 top-1.5 text-[10px] text-slate-600">sem animações · adicione pelo painel ao lado</span>}
        </TrackRow>
      ))}
      {behind.length > 0 && Array.from({ length: bL.lanes }).map((_, lane) => (
        <TrackRow key={`bh${lane}`} icon={lane === 0 ? <IconBehind /> : undefined} tone="slate" title="Atrás da pessoa" height={28}>
          {behind.filter(it => bL.get(it) === lane).map(({ b, index, start, end }) => {
            const saved = savedBehind[index];
            const label = b.kind === 'words' ? `ATRÁS · ${b.words.map(w => w.t).join(' ')}` : `ATRÁS · ${fname(b.src)}`;
            return (
              <Chip
                key={`b${index}`}
                start={start}
                end={end}
                max={duration}
                min={0.5}
                label={label}
                title={`${label}
${fmt(start)} → ${fmt(end)}
arraste para mover · bordas aparam`}
                selected={isSel({ kind: 'behind', index })}
                dirty={!!saved && (saved.start !== b.start || saved.dur !== b.dur)}
                tone="border-sky-400/50 bg-sky-500/15 text-sky-100"
                handles={['move', 'l', 'r']}
                snap={snapWord}
                onSelect={() => onSelect({ kind: 'behind', index })}
                onBegin={beginDrag}
                onChange={(s, e) => setBehind(index, s, e)}
                onEnd={endDrag}
              />
            );
          })}
        </TrackRow>
      ))}
      {Array.from({ length: images.length ? iL.lanes : 1 }).map((_, lane) => (
        <TrackRow key={`img${lane}`} icon={lane === 0 ? <IconImage /> : undefined} tone="amber" title="Imagens / tela dividida" height={28}>
          {images.filter(it => iL.get(it) === lane).map(it => {
            const saved = it.kind === 'split' ? savedSplit[it.index] : savedIns[it.index];
            return (
              <Chip
                key={`${it.kind}${it.index}`}
                start={it.start}
                end={it.end}
                max={duration}
                label={`${it.kind === 'split' ? 'SPLIT · ' : ''}${fname(it.src)}`}
                title={`${fname(it.src)}\n${fmt(it.start)} → ${fmt(it.end)}\narraste para mover · bordas aparam · ímã nas emendas`}
                selected={isSel({ kind: it.kind, index: it.index })}
                dirty={!!saved && (saved.start !== it.start || saved.end !== it.end)}
                tone="border-amber-400/50 bg-amber-500/15 text-amber-100"
                handles={['move', 'l', 'r']}
                snap={snap}
                onSelect={() => onSelect({ kind: it.kind, index: it.index })}
                onBegin={beginDrag}
                onChange={(s, e) => setImage(it.kind, it.index, s, e)}
                onEnd={endDrag}
              />
            );
          })}
          {!images.length && <span className="absolute left-2 top-1.5 text-[10px] text-slate-600">sem imagens</span>}
        </TrackRow>
      ))}
      {!!ed.transitions?.length && (
        <TrackRow icon={<IconFlash />} tone="slate" title="Transições" height={22}>
          <TransitionMarks at={ed.transitions.map(t => t.at)} />
        </TrackRow>
      )}
      {ed.soundtrack.enabled && (
        <TrackRow icon={<IconMusic />} tone="olive" title="Trilha" height={24}>
          <div className="absolute bottom-[3px] top-[3px] flex items-center truncate rounded-md border border-lime-400/40 bg-lime-500/10 px-2 text-[10px] font-bold text-lime-100" style={{ left: 0, width: Math.max(duration * pps, 10) }}>
            {fname(ed.soundtrack.file)} · vol {ed.soundtrack.volume}
          </div>
        </TrackRow>
      )}
    </TimelineShell>
  );
}

function TransitionMarks({ at }: { at: number[] }) {
  const { pps } = useTimeline();
  return (
    <>
      {at.map((t, i) => (
        <div key={i} className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[2px] bg-slate-200/80" style={{ left: t * pps }} title={`transição em ${fmt(t)}`} />
      ))}
    </>
  );
}
